import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { FormatterTimezoneContext } from '../../shared/format/formatter-timezone';
import { useFormatters } from '../../shared/format/use-formatters';
import { Badge } from '../../shared/ui/badge';
import { ButtonLink } from '../../shared/ui/button';
import { EntityLogo } from '../../shared/ui/entity-logo';
import { landingSampleItem as item } from './landing-sample';

const sampleTimezone = 'America/Sao_Paulo';
const sampleNow = new Date(Date.parse(item.oddsAt) + 10 * 60 * 1000).toISOString();
const sampleFormatting = { timezone: sampleTimezone, now: sampleNow };
const games = item.history ?? [];
const line = item.line ?? 0;

const resultClass = {
  won: 'border-positive/45 bg-positive-soft text-positive',
  lost: 'border-negative/40 bg-negative-soft text-negative',
};

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div>
    <dt className="text-xs text-muted">{label}</dt>
    <dd className="mt-1 text-lg font-semibold text-content tabular-nums">{value}</dd>
  </div>
);

const FixtureSide = ({ name, logoUrl }: { name: string; logoUrl: string | null }) => (
  <span className="inline-flex items-center gap-1">
    <EntityLogo name={name} url={logoUrl} className="size-4" />
    {name}
  </span>
);

const SampleCard = () => {
  const { t } = useTranslation();
  const { locale } = useParams();
  const { formatNumber, formatPrice, formatLocalTime, formatRelativeTime } = useFormatters();
  const market = t('radar.markets.corners');
  const side = `${t('radar.sides.over')} ${formatNumber(line)}`;
  const period = t('radar.full');
  const probability = formatNumber(item.probability, {
    style: 'percent',
    maximumFractionDigits: 0,
  });
  const probabilityLabel = t('ticket.historicalEstimate');
  return (
    <article className="flex h-full flex-col rounded-xl bg-surface ring-1 ring-line">
      <div className="flex flex-1 flex-col gap-3 px-4 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-muted min-w-0 flex-1">
            <strong className="flex items-center gap-1.5 font-medium text-content">
              <EntityLogo
                name={item.competition}
                url={item.competitionLogoUrl}
                className="size-4"
              />
              {item.competition}
            </strong>
            <span>
              <time dateTime={item.startsAt}>{formatLocalTime(item.startsAt, 'L LT')}</time>
            </span>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            <input
              type="checkbox"
              className="size-5 shrink-0 accent-accent disabled:cursor-not-allowed disabled:opacity-40"
              disabled
              aria-label={t('study.selectLine', {
                match: `${item.homeName} vs ${item.awayName}`,
                line: `${market} · ${side} · ${period}`,
              })}
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span
            aria-label={`${probabilityLabel}: ${probability}`}
            className="flex size-12 shrink-0 items-center justify-center rounded-full bg-accent-soft text-sm font-semibold text-accent-ink tabular-nums"
          >
            {probability}
          </span>
          <div className="min-w-0 flex-1 basis-40">
            <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-sm font-semibold text-content">
              <FixtureSide name={item.homeName} logoUrl={item.homeLogoUrl} />{' '}
              <span className="text-muted">vs</span>{' '}
              <FixtureSide name={item.awayName} logoUrl={item.awayLogoUrl} />
            </p>
          </div>
          <span className="ml-auto">
            <Badge tone="green">
              <span title={t('radar.ev')}>EV</span>{' '}
              <strong>
                {formatNumber(item.expectedValue, {
                  style: 'percent',
                  signDisplay: 'exceptZero',
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}
              </strong>
            </Badge>
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone="accent">{market}</Badge>
          <p className="rounded-md bg-accent px-2 py-0.5 text-xs font-semibold text-on-accent">
            {side}
          </p>
          <span className="text-xs text-muted">{period}</span>
        </div>
        <div className="grid gap-1.5">
          <p className="flex w-full items-center gap-1.5 text-xs font-medium text-muted">
            <span>{t('radar.openSample')}</span>
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="size-3.5"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </p>
          <div className="grid gap-1">
            <div className="flex items-center gap-2">
              <EntityLogo name={item.homeName} url={item.homeLogoUrl} className="size-4" />
              <ol
                className="m-0 grid min-w-0 flex-1 list-none gap-1 p-0 grid-cols-[repeat(10,minmax(0,40px))]"
                aria-label={t('radar.matchHistory', { team: item.homeName })}
              >
                {games.map((game) => (
                  <li
                    key={game.fixtureId}
                    data-result={(game.value ?? 0) > line ? 'won' : 'lost'}
                    className={`grid h-7 min-w-0 place-items-center rounded-md border text-xs font-bold tabular-nums ${resultClass[(game.value ?? 0) > line ? 'won' : 'lost']}`}
                  >
                    <span>{formatNumber(game.value ?? 0)}</span>
                    <time className="sr-only" dateTime={game.startsAt}>
                      {formatLocalTime(game.startsAt, 'L')}
                    </time>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
        <div className="flex items-end justify-between gap-3">
          <dl className="flex flex-wrap gap-4">
            <Stat
              label={t('radar.average')}
              value={formatNumber(item.average, { maximumFractionDigits: 2 })}
            />
            <Stat
              label={t('radar.historicalHits')}
              value={`${formatNumber(item.hits)}/${formatNumber(item.known)}`}
            />
          </dl>
          <div className="text-right">
            <p className="text-xs text-muted">{t('radar.offeredOdds')}</p>
            <strong className="block text-2xl font-bold text-content tabular-nums">
              {formatPrice(item.price)}
            </strong>
          </div>
        </div>
      </div>
      <div className="mt-4 space-y-3 border-t border-line px-4 py-3">
        <p className="text-xs text-muted">
          {t('radar.oddsUpdated')}:{' '}
          <time dateTime={item.oddsAt} title={formatLocalTime(item.oddsAt, 'L LT')}>
            {formatRelativeTime(item.oddsAt)}
          </time>
        </p>
        <div className="flex flex-wrap justify-end gap-2">
          <ButtonLink
            variant="secondary"
            size="sm"
            to={`/${locale}/football/matches/${item.fixtureId}`}
          >
            {t('radar.analyzeMatch')}
          </ButtonLink>
        </div>
      </div>
    </article>
  );
};

export const LandingHeroCard = () => (
  <FormatterTimezoneContext value={sampleFormatting}>
    <div className="rounded-xl bg-raised p-3 sm:p-4">
      <SampleCard />
    </div>
  </FormatterTimezoneContext>
);
